/**
 * ProviderApprovalSettings - Manage who can use ChartMind when scanning clinic QR (Phase 3).
 * Chartmind-supervisor and facility can set policy and allowed provider list.
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Delete, Save } from '@mui/icons-material';
import { getProviderApproval, setProviderApproval } from '@healthdesk/shared-services';

const POLICIES = [
  { value: 'approve_all', label: 'Approve all providers' },
  { value: 'deny_all', label: 'Deny all' },
  { value: 'approve_if_on_list', label: 'Approve if on list' },
  { value: 'approve_with_expiry', label: 'Approve with expiry (grants)' },
];

const ProviderApprovalSettings = ({ clinicId }) => {
  const [config, setConfig] = useState({ policy: 'approve_if_on_list', allowedProviderIds: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newUid, setNewUid] = useState('');

  useEffect(() => {
    if (!clinicId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getProviderApproval(clinicId)
      .then((data) => {
        if (!cancelled) setConfig(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [clinicId]);

  const handleSave = async () => {
    if (!clinicId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await setProviderApproval(clinicId, config);
      setSuccess('Saved.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddUid = () => {
    const uid = (newUid || '').trim();
    if (!uid || config.allowedProviderIds.includes(uid)) return;
    setConfig((prev) => ({
      ...prev,
      allowedProviderIds: [...prev.allowedProviderIds, uid],
    }));
    setNewUid('');
  };

  const handleRemoveUid = (uid) => {
    setConfig((prev) => ({
      ...prev,
      allowedProviderIds: prev.allowedProviderIds.filter((id) => id !== uid),
    }));
  };

  if (!clinicId) return null;
  if (loading) {
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent><CircularProgress size={24} /> Loading...</CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title="Provider access (ChartMind)"
        subheader="When a provider scans the clinic QR, access is allowed or denied by this policy."
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Policy</InputLabel>
          <Select
            value={config.policy}
            label="Policy"
            onChange={(e) => setConfig((prev) => ({ ...prev, policy: e.target.value }))}
          >
            {POLICIES.map((p) => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {(config.policy === 'approve_if_on_list' || config.policy === 'approve_with_expiry') && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Allowed provider IDs (user UIDs)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="Add user ID"
                value={newUid}
                onChange={(e) => setNewUid(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddUid())}
              />
              <Button variant="outlined" onClick={handleAddUid}>Add</Button>
            </Box>
            <List dense>
              {(config.allowedProviderIds || []).map((uid) => (
                <ListItem
                  key={uid}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => handleRemoveUid(uid)}>
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemText primary={uid} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{ mt: 2 }}
        >
          Save
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProviderApprovalSettings;
